import type { AxiosError } from 'axios';
import { ErrorInterceptorTemplate, UnknownError } from 'viewup-lib/common';
import { isCanceledRequest } from './canceledRequest';
import { notifyRateLimit } from './rateLimitSignal';
import { resolveRetryAt } from './tooManyRequests';

export class ErrorInterceptor extends ErrorInterceptorTemplate {
  /**
   * 취소된 요청을 SDK 템플릿보다 **먼저** 걸러낸다.
   *
   * 템플릿의 `!e.request → NetworkError` 분기가 `CancelToken.source().cancel()` 로 만든
   * 취소까지 전송 실패로 승격시키는데, `NetworkError` 는 `defaultQueryPolicy` 가 React 에러
   * 바운더리로 throw 하는 유일한 타입이라 언마운트·큐 정리로 인한 정상 취소가 글로벌 에러
   * 화면이 된다. 판정은 `./canceledRequest` 가 소유한다 — 리포트 제외 조건과 같은 기준을
   * 써야 한 쪽만 고쳐지는 드리프트가 안 생긴다.
   *
   * 원본 에러를 그대로 reject 한다 — 취소는 호출 측(react-query·`useClickCampaignList` 루프)이
   * `isCancel` 로 식별해 조용히 넘기는 흐름이라 타입을 바꾸면 그 식별이 깨진다.
   */
  override get responseInterceptorOnRejected(): (e: unknown) => Promise<never> {
    const delegate = super.responseInterceptorOnRejected;

    return (error: unknown) => {
      if (isCanceledRequest(error)) return Promise.reject(error);

      return delegate(error);
    };
  }

  protected override handleError(error: AxiosError<{ code: string }, any>): void {
    if (error.response?.status === 429 && error.response.data.code === 'tooManyRequests') {
      // store 를 직접 부르지 않는 이유는 `rateLimitSignal.ts` 주석 참고 — 이 클래스는 SSR
      // 인스턴스(`ssrErrorInterceptor`)로도 재사용되고, app 레이어 import 는 이 모듈을
      // 쓰는 모든 테스트의 mock 세팅을 깨뜨린다.
      notifyRateLimit(resolveRetryAt(error.response.data));
      return;
    }

    if (error.response?.data.code === 'unknown') {
      throw new UnknownError(error.message);
    }
  }
}
